import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { UserRole } from '../types/role';
import {
  getBarberSubscriptionByBarberId,
  getShopSubscriptionByShopId,
  retrieveStripeSubscription,
} from '../middleware/registration_service/getRequests';
import { StripeSubscription } from '../types/stripePayments';
import { SHOP_OWNER_PLANS } from '../defaults/SubscriptionPlans';

const ACTIVE_STATUSES = ['active', 'trialing'];

export interface SubscriptionAccess {
  /** True while the subscription status is being fetched. */
  loading: boolean;
  /** True if the partner's (shop owner or barber) subscription is active or trialing. */
  isActive: boolean;
  /** Number of barber seats allowed by the shop's subscription model, if applicable. */
  barberSeatCount: number | null;
}

/**
 * Looks up the live status of a Stripe subscription. Returns `null` if no
 * `stripeSubscriptionId` is available or the lookup fails, so callers can
 * fall back to treating access as inactive.
 */
const fetchLiveStripeStatus = async (
  stripeSubscriptionId: string | undefined,
  token: string
): Promise<StripeSubscription['status'] | null> => {
  if (!stripeSubscriptionId) return null;
  try {
    const response = await retrieveStripeSubscription(
      stripeSubscriptionId,
      token
    );
    const stripeSubscription: StripeSubscription = response.data;
    return stripeSubscription?.status ?? null;
  } catch (err) {
    console.error('Failed to retrieve live Stripe subscription:', err);
    return null;
  }
};

/**
 * Resolves feature access for the current user based on their subscription
 * model and status. Shop owners and shop barbers are gated on the shop's
 * subscription, independent barbers are gated on their own subscription.
 *
 * The local subscription record (fetched via `getShopSubscriptionByShopId` /
 * `getBarberSubscriptionByBarberId`) is only used to resolve the Stripe
 * subscription ID and plan metadata (e.g. barber seat count); the gating
 * `isActive` decision is always based on the live status returned by
 * `retrieveStripeSubscription`, not the local record's cached `status`.
 */
export function useSubscriptionAccess(role: string | null): SubscriptionAccess {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [barberSeatCount, setBarberSeatCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const reset = () => {
      if (cancelled) return;
      setIsActive(false);
      setBarberSeatCount(null);
    };

    const fetchSubscription = async () => {
      setLoading(true);
      try {
        const token = await getAccessTokenSilently();
        if (role === UserRole.SHOP_OWNER || role === UserRole.SHOP_BARBER) {
          const shopIdItem = localStorage.getItem('reg_shop_id');
          if (!shopIdItem) {
            reset();
            return;
          }
          const res = await getShopSubscriptionByShopId(
            BigInt(shopIdItem),
            token
          );
          if (cancelled) return;
          const liveStatus = await fetchLiveStripeStatus(
            res.data?.stripeSubscriptionId,
            token
          );
          if (cancelled) return;
          setIsActive(!!liveStatus && ACTIVE_STATUSES.includes(liveStatus));
          const matched = SHOP_OWNER_PLANS.find(
            (p) => p.name === res.data?.name
          );
          setBarberSeatCount(
            res.data?.barberSeatCount ?? matched?.barberSeatCount ?? null
          );
        } else if (role === UserRole.BARBER) {
          const partnerItem = localStorage.getItem('reg_partner_id');
          if (!partnerItem) {
            reset();
            return;
          }
          const res = await getBarberSubscriptionByBarberId(
            BigInt(partnerItem),
            token
          );
          if (cancelled) return;
          const liveStatus = await fetchLiveStripeStatus(
            res.data?.stripeSubscriptionId,
            token
          );
          if (cancelled) return;
          setIsActive(!!liveStatus && ACTIVE_STATUSES.includes(liveStatus));
          setBarberSeatCount(null);
        } else {
          reset();
        }
      } catch (err) {
        console.error('Failed to load subscription for access check:', err);
        reset();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSubscription();

    return () => {
      cancelled = true;
    };
  }, [role]);

  return { loading, isActive, barberSeatCount };
}
